import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { PermissionService } from '@services/permission.service';
import { JwtService } from '@services/jwt.service';
import { APP_PATHS } from '@/app/utils';

/*
  Route guard that enforces IsView permission for the requested URL.
  Asynchronously awaits permission loading before evaluating access.
 */
export const MenuRightGuard: CanActivateFn = (route, state) => {
  const permService = inject(PermissionService);
  const jwtService  = inject(JwtService);
  const router      = inject(Router);

  const isSuperAdmin = jwtService.isSuperAdmin();

  if (isSuperAdmin) return true;

  const requestedUrl = state.url.split('?')[0];

  return permService.ensureLoaded(isSuperAdmin).pipe(
    map(() => {
      if (permService.hasNoAccess(isSuperAdmin)) {
        return router.createUrlTree([APP_PATHS.ACCESS_DENIED]);
      }

      if (permService.canView(requestedUrl, isSuperAdmin)) {
        return true;
      }

      // Fall back to the user's default accessible route when the requested page is not allowed.
      const fallbackRoute = permService.getDefaultRoute(isSuperAdmin);
      const normalisedRequested = permService.normalise(requestedUrl).toLowerCase();
      const normalisedFallback = permService.normalise(fallbackRoute).toLowerCase();

      if (
        fallbackRoute &&
        normalisedFallback !== normalisedRequested &&
        normalisedFallback !== permService.normalise(APP_PATHS.ACCESS_DENIED).toLowerCase()
      ) {
        return router.createUrlTree([fallbackRoute]);
      }

      return router.createUrlTree([APP_PATHS.ACCESS_DENIED]);
    }),
    catchError(() => {
      return of(router.createUrlTree([APP_PATHS.ACCESS_DENIED]));
    })
  );
};
